import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { PulseLoader } from "react-spinners";
import { Bell, ExternalLink } from "lucide-react";
import { useLocation, useNavigate } from "react-router";
import { DashboardConfigWidget, IGetTimeOffBalance } from "../types";
import { renderWebDashboardWidget } from "../config";
import { RootState, useAppDispatch } from "../redux/store";
import { getBalanceByCategory, getTimeOffRequestList } from "../redux/features/timeoff";
import { getMyTodaysSchedule } from "../redux/features/schedule/ScheduleApi";
import { fetchDashboardWidgetConfig } from "../redux/features/dashboard";
import { getActionRequiredCount } from "../redux/features/mobileNotifications";
import { DateTimeUtil, getPrimaryActorId } from "../utils";
import { getWhoIsWorking } from "../redux/features/whoIsIn";
import { clearGlobalBackHandler } from "../hooks";
import { WebDashboardRibbon, WebQuickActionsTile } from "../components/web/webDashboard";
import { isPrivilege, UserPrivileges } from "../rules";
import { providePhpRoute } from "../routes/phpRoutes";
import { PhpRoute } from "../enums";
import {
    ACTION_REQUIRED_DASHBOARD_WIDGET_CODES,
    DEFAULT_DASHBOARD_WIDGET_ORDER,
    DEFAULT_DASHBOARD_WIDGET_VISIBILITY,
    ensureActionRequiredDashboardWidget,
    getDashboardDateRange,
    isAnyDashboardWidgetEnabled,
    isDashboardWidgetEnabled,
} from "../utils/dashboardWidgets";

const DASHBOARD_LOADING_MESSAGES = [
    "Your dashboard is being prepared...",
    "Fetching your account details...",
    "Almost ready...",
];

const WebDashboard: React.FC = () => {
    const user = useSelector((state: RootState) => state.user.user);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isManager = user.is_manager;

    const { employees: whoIsWorkingEmp, isLoading: whoIsWorkingLoading } = useSelector(
        (state: RootState) => state.whoIsIn
    );
    const { unreadNotifCount } = useSelector((state: RootState) => state.common);
    const { dashboardConfigWidgets, dashboardConfigCustomLinks, dashboardConfigLoadedForUserId, dashboardConfigError } = useSelector(
        (state: RootState) => state.dashboard
    );
    const { actionRequiredCount, isActionRequiredCountLoading } = useSelector(
        (state: RootState) => state.mobileNotifications
    );
    const [hasCompletedDashboardConfigRequest, setHasCompletedDashboardConfigRequest] = React.useState(false);
    const dashboardUserId = getPrimaryActorId(user);
    const currentUserId = dashboardUserId != null && dashboardUserId !== -1 ? String(dashboardUserId) : null;
    const loadedDashboardConfigUserId = dashboardConfigLoadedForUserId != null
        ? String(dashboardConfigLoadedForUserId)
        : null;
    const hasResolvedDashboardConfig = Boolean(
        currentUserId && loadedDashboardConfigUserId === currentUserId
    );
    const shouldUseDashboardConfig = hasResolvedDashboardConfig && !dashboardConfigError;
    const configuredDashboardWidgets = shouldUseDashboardConfig
        ? dashboardConfigWidgets
        : DEFAULT_DASHBOARD_WIDGET_ORDER;
    const orderedDashboardWidgets = ensureActionRequiredDashboardWidget(configuredDashboardWidgets);
    const customLinks = shouldUseDashboardConfig ? dashboardConfigCustomLinks : [];
    const shouldWaitForDashboardConfig = Boolean(currentUserId && !hasCompletedDashboardConfigRequest);
    const isDashboardLoading = !currentUserId || shouldWaitForDashboardConfig;
    const shouldFetchWhoIsIn = isDashboardWidgetEnabled(
        orderedDashboardWidgets,
        shouldUseDashboardConfig,
        "who_is_in",
        DEFAULT_DASHBOARD_WIDGET_VISIBILITY.WHO_IS_IN
    );
    const shouldFetchMySchedule = isDashboardWidgetEnabled(
        orderedDashboardWidgets,
        shouldUseDashboardConfig,
        "my_schedule",
        DEFAULT_DASHBOARD_WIDGET_VISIBILITY.MY_SCHEDULE
    );
    const shouldFetchTimeOff = isDashboardWidgetEnabled(
        orderedDashboardWidgets,
        shouldUseDashboardConfig,
        "module_timeoff",
        DEFAULT_DASHBOARD_WIDGET_VISIBILITY.MODULE_TIMEOFF
    );
    const shouldFetchActionRequired = isAnyDashboardWidgetEnabled(
        orderedDashboardWidgets,
        shouldUseDashboardConfig,
        ACTION_REQUIRED_DASHBOARD_WIDGET_CODES,
        DEFAULT_DASHBOARD_WIDGET_VISIBILITY.ACTION_REQUIRED
    );

    const userPrivileges = user.employee_access_role_privileges;
    const isAdmin = isPrivilege({
        privileges: userPrivileges,
        rule: UserPrivileges.EMPLOYEE_PRIV_ADMIN,
    });
    const hasScheduleToolPrivilege =
        isPrivilege({
            privileges: userPrivileges,
            rule: UserPrivileges.EMPLOYEE_PRIV_SCHEDULE_TOOL,
        }) || isAdmin;

    const fetchInitialDashboardData = useCallback(async (forceRefresh: boolean = false) => {
        const promises: Promise<any>[] = [];

        if (shouldFetchWhoIsIn) {
            promises.push(
                dispatch(
                    getWhoIsWorking({
                        current_datetime: DateTimeUtil.formatDateToMySQL(new Date()),
                        forceRefresh,
                    })
                )
            );
        }

        if (shouldFetchMySchedule) {
            promises.push(
                dispatch(
                    getMyTodaysSchedule({
                        forceRefresh,
                    })
                )
            );
        }

        if (dashboardUserId != null && dashboardUserId !== -1 && shouldFetchTimeOff) {
            const body: IGetTimeOffBalance = {
                category_code: "",
                employee_id: String(dashboardUserId),
                forceRefresh,
            };

            const dateRange = getDashboardDateRange();

            const getTimeOffRequestsBody = {
                start_date: dateRange[0],
                end_date: dateRange[dateRange.length - 1],
                employee_id: String(dashboardUserId),
                category_code: "",
                forceRefresh,
            };

            promises.push(
                dispatch(getBalanceByCategory(body)),
                dispatch(getTimeOffRequestList(getTimeOffRequestsBody))
            );
        }

        if (shouldFetchActionRequired) {
            promises.push(dispatch(getActionRequiredCount({ forceRefresh })));
        }

        await Promise.all(promises);
    }, [dashboardUserId, dispatch, shouldFetchActionRequired, shouldFetchMySchedule, shouldFetchTimeOff, shouldFetchWhoIsIn]);

    useEffect(() => {
        if (currentUserId && hasCompletedDashboardConfigRequest) {
            fetchInitialDashboardData(false);
        }
    }, [currentUserId, fetchInitialDashboardData, hasCompletedDashboardConfigRequest]);

    useEffect(() => {
        if (!currentUserId) {
            setHasCompletedDashboardConfigRequest(false);
            return;
        }

        let isActive = true;

        const loadDashboardConfig = async () => {
            setHasCompletedDashboardConfigRequest(false);
            await dispatch(fetchDashboardWidgetConfig());

            if (isActive) {
                setHasCompletedDashboardConfigRequest(true);
            }
        };

        loadDashboardConfig();

        return () => {
            isActive = false;
        };
    }, [currentUserId, dispatch]);

    const [loadingMessage, setLoadingMessage] = React.useState(0);

    useEffect(() => {
        if (isDashboardLoading) {
            const interval = setInterval(() => {
                setLoadingMessage((prev) => (prev + 1) % DASHBOARD_LOADING_MESSAGES.length);
            }, 1500);

            return () => clearInterval(interval);
        }
    }, [isDashboardLoading]);

    const isWidgetVisible = (widget: DashboardConfigWidget, fallbackVisibility?: boolean): boolean => {
        return isDashboardWidgetEnabled(
            orderedDashboardWidgets,
            shouldUseDashboardConfig,
            widget.widget_code,
            fallbackVisibility
        );
    };

    if (isDashboardLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen w-full bg-white">
                <PulseLoader
                    color="#3B82F6"
                    loading={true}
                    size={12}
                    speedMultiplier={0.8}
                    margin={4}
                    aria-label="Loading Dashboard"
                />
                <div
                    className="mt-6 text-gray-600 text-center text-base font-light"
                    style={{
                        animation: "fadeIn 0.3s ease-out",
                    }}
                >
                    {DASHBOARD_LOADING_MESSAGES[loadingMessage]}
                </div>
                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(5px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
                <div className="text-center mb-8 relative">
                    <div className="absolute top-0 right-0">
                        <button
                            onClick={() => navigate("/m-notifications")}
                            className="p-2 rounded-full bg-white shadow-sm border border-gray-200 hover:shadow-md hover:bg-gray-50 transition-all duration-200 relative"
                            title="Notifications"
                        >
                            <Bell className="w-6 h-6 text-gray-600" />
                            {unreadNotifCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                                    {unreadNotifCount > 99 ? "99+" : unreadNotifCount}
                                </span>
                            )}
                        </button>
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Dashboard</h1>
                    <div className="mt-2 max-w-3xl mx-auto">
                        <p className="text-xl text-gray-600 text-center">Welcome back, {user.os_emp_first_name || "User"}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {orderedDashboardWidgets.map((widget, index) => {
                        const renderedWidget = renderWebDashboardWidget({
                            widget,
                            isWidgetVisible,
                            skipMealBreakRefreshKey: location.key,
                            actionRequiredCount,
                            isActionRequiredCountLoading,
                            whoIsWorkingEmp: whoIsWorkingEmp || [],
                            whoIsWorkingLoading: whoIsWorkingLoading || false,
                            WHO_IS_IN: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.WHO_IS_IN,
                            TIMECARD: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.TIMECARD,
                            MODULE_TIMEOFF: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.MODULE_TIMEOFF,
                            MY_SCHEDULE: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.MY_SCHEDULE,
                            REMOTE_WORK_COUNTER: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.REMOTE_WORK_COUNTER,
                            PARIS_WORK_COUNTER: DEFAULT_DASHBOARD_WIDGET_VISIBILITY.PARIS_WORK_COUNTER,
                            canShowTeamTimesheet: Boolean(isManager),
                            canShowTimecardWidget: user?.allowwebclock_nogeo === true,
                            canShowShiftPlanner: Boolean(hasScheduleToolPrivilege),
                            onOpenWidgetUrl: (url: string) => {
                                navigate(url);
                            },
                            onOpenSchedule: () => {
                                navigate("/m-calendar");
                            },
                            onOpenTeamTimesheet: () => {
                                clearGlobalBackHandler();
                                navigate("/m-manager-timesheet", { state: { from: "/web-dashboard" } });
                            },
                            onOpenShiftPlanner: () => {
                                navigate("/scheduling-tool");
                            },
                            onOpenTimeOff: () => {
                                navigate("/m-timeoff", { state: { from: "/web-dashboard" } });
                            },
                            onOpenNotifications: () => {
                                navigate("/m-notifications");
                            },
                        });

                        if (!renderedWidget) {
                            return null;
                        }

                        return (
                            <React.Fragment key={`${widget.widget_code || "dashboard-widget"}-${widget.sort_order || index}`}>
                                {renderedWidget}
                            </React.Fragment>
                        );
                    })}
                </div>

                {customLinks.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                        <WebQuickActionsTile links={customLinks} />
                    </div>
                )}

                <WebDashboardRibbon
                    title="Switch to legacy dashboard"
                    description="Click to access the legacy dashboard in a new tab"
                    onClick={() => {
                        const routeUrl = providePhpRoute(PhpRoute.HOME);
                        window.open(routeUrl, "_blank");
                    }}
                    icon={ExternalLink}
                    variant="info"
                />
            </div>
        </div>
    );
};

export default WebDashboard;
